import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PageShell, { PageHero, SectionTitle, FeatureGrid, StatsRow } from './PageShell';
import { elevePage } from '../mockPages';
import { ArrowRight, ArrowUpRight, BookOpen, GraduationCap, CheckCircle2 } from 'lucide-react';
import FeaturedDemosSection from '../components/FeaturedDemosSection';
import { useAuth } from '../context/AuthContext';

export default function ElevePage() {
  const navigate = useNavigate();
  const { user, openRegister } = useAuth();
  const onCreateAccount = () => {
    if (!user) { openRegister(); return; }
    navigate('/cours/eleve/voiture');
  };
  return (
    <PageShell>
      <PageHero data={elevePage.hero} />

      <FeaturedDemosSection variant="dashboard" />

      {/* 6 steps timeline */}
      <section className="max-w-7xl mx-auto px-4 lg:px-6 mt-12 sm:mt-20">
        <SectionTitle
          kicker="Ton parcours en 6 étapes"
          title="De l'inscription au permis probatoire."
          sub="Une feuille de route claire et progressive, validée par des moniteurs ECSR titulaires."
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {elevePage.steps.map((s, i) => (
            <div key={i} className="group rounded-xl sm:rounded-2xl border border-white/10 bg-gradient-to-br from-[#141416] to-[#0c0c0d] p-4 sm:p-6 hover:border-red-700/40 transition">
              <div className="flex items-start justify-between mb-2 sm:mb-3">
                <span className="font-mono-spec text-[30px] sm:text-[40px] lg:text-[56px] leading-none text-red-500/80 group-hover:text-red-500 transition">{s.n}</span>
                <span className="text-[9.5px] sm:text-[10px] uppercase tracking-[0.18em] text-white/40 font-semibold">{s.label}</span>
              </div>
              <h3 className="font-serif-editorial text-white text-[18px] sm:text-[22px] leading-tight">{s.title}</h3>
              <p className="text-white/60 text-[12.5px] sm:text-[13.5px] mt-2 sm:mt-3 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Categories table */}
      <section className="max-w-7xl mx-auto px-4 lg:px-6 mt-14 sm:mt-24">
        <SectionTitle
          kicker="Catégories"
          title="Choisis ton permis voiture."
          sub="Six chemins selon ton âge, ton projet et ton budget. Tous mènent au même examen ETG officiel."
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {elevePage.parcours.map((p, i) => (
            <div key={i} className={`relative rounded-xl sm:rounded-2xl border p-5 sm:p-7 transition ${p.pop ? 'border-red-700/50 bg-gradient-to-br from-red-950/30 to-[#0d0d0e]' : 'border-white/10 bg-[#0d0d0e] hover:border-red-700/40'}`}>
              {p.pop && (
                <span className="absolute -top-2 left-5 sm:left-6 px-2 py-0.5 rounded-md bg-red-600 text-white text-[10px] font-bold tracking-wider">POPULAIRE</span>
              )}
              <div className="flex items-center justify-between mb-3">
                <span className="font-serif-editorial text-white text-[28px] sm:text-[36px] leading-none">{p.code}</span>
                <span className="text-[10.5px] sm:text-[11px] uppercase tracking-wider text-white/40 font-semibold">{p.age}</span>
              </div>
              <p className="text-white/65 text-[12.5px] sm:text-[14px] leading-relaxed">{p.desc}</p>
              <Link to={p.to} data-testid={`parcours-link-${p.code.replace('+', '')}`} className="mt-4 sm:mt-5 inline-flex items-center gap-1.5 text-red-400 text-[12.5px] sm:text-[13px] font-semibold hover:text-red-300">En savoir plus <ArrowUpRight size={13} /></Link>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-4 lg:px-6 mt-14 sm:mt-24">
        <SectionTitle kicker="Tes outils" title="Ce qui rend Code Voiture différent." />
        <FeatureGrid items={elevePage.features} />
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 lg:px-6 mt-14 sm:mt-24">
        <div className="rounded-2xl sm:rounded-3xl border border-red-900/30 bg-gradient-to-br from-red-950/30 via-[#160a0a] to-[#0d0d0e] p-5 sm:p-8 lg:p-14 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-5 sm:gap-6">
          <div>
            <div className="flex items-center gap-2 text-red-400 mb-3"><GraduationCap size={18} /><span className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] font-bold">Prêt à commencer ?</span></div>
            <h3 className="font-serif-editorial text-white text-[22px] sm:text-[30px] lg:text-[42px] leading-[1.05] sm:leading-[1.02] max-w-2xl">Ton premier dossier ETG t’attend.</h3>
            <p className="text-white/65 text-[13px] sm:text-[14.5px] mt-3 max-w-xl">Crée ton compte en 30 secondes et accède immédiatement aux 9 dossiers ETG, 31 enquêtes magazine et au livret 38 compétences.</p>
          </div>
          <div className="flex gap-2 sm:gap-3 flex-wrap">
            <button onClick={onCreateAccount} data-testid="eleve-cta-register" className="h-11 sm:h-12 px-5 sm:px-6 rounded-full bg-red-600 hover:bg-red-500 text-white text-[13px] sm:text-[14px] font-semibold inline-flex items-center gap-2 shadow-lg shadow-red-900/40">Créer mon compte gratuit <ArrowRight size={15} /></button>
            <Link to="/demos-interactives" data-testid="eleve-cta-demo" className="h-11 sm:h-12 px-5 sm:px-6 rounded-full border border-white/15 text-white text-[13px] sm:text-[14px] font-medium hover:bg-white/5 inline-flex items-center gap-2"><BookOpen size={14} /> Voir une démo</Link>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
